// src/hooks/useNotifications.js
// All notifications are now direct docs: { recipientUid, read: false, ... }
// Broadcast fan-out is handled server-side by Cloud Function 'fanOutBroadcastNotification'.
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  writeBatch,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

const useNotifications = () => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Single real-time query — only this user's direct notifications
  useEffect(() => {
    if (!uid) { setLoading(false); return; }

    const q = query(
      collection(db, 'notifications'),
      where('recipientUid', '==', uid)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setNotifications(items);
        setLoading(false);
      },
      (err) => {
        console.error('[useNotifications] query error:', err.code, err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  const unreadCount = notifications.filter(n => !n.read).length;

  /** Mark every unread notification as read in a single batch. */
  const markAllRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.read);
    if (!unread.length) return;
    const batch = writeBatch(db);
    unread.forEach(n => {
      batch.update(doc(db, 'notifications', n.id), { read: true });
    });
    await batch.commit();
  }, [notifications]);

  /** Mark a single notification as read. */
  const markRead = useCallback(async (notifId) => {
    const notif = notifications.find(n => n.id === notifId);
    if (!notif || notif.read) return;
    await updateDoc(doc(db, 'notifications', notifId), { read: true });
  }, [notifications]);

  return { notifications, unreadCount, loading, markAllRead, markRead };
};

export default useNotifications;
